import React from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Shield, CheckCircle, Phone, Car, Wrench, DollarSign, AlertCircle, ArrowRight } from 'lucide-react';

const Warranty = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  const basicFeatures = [
    'Engine Components',
    'Transmission Coverage',
    'Drive Axle',
    'Basic Roadside Assistance',
    'Towing Benefits'
  ];

  const premiumFeatures = [
    'All Basic Protection Features',
    'Electrical System',
    'Steering Components',
    'Braking System',
    'Air Conditioning',
    'Enhanced Roadside Package'
  ];

  const ultimateFeatures = [
    'All Premium Protection Features',
    'Fuel System Components',
    'Cooling System',
    'High-Tech Components',
    'Enhanced Labor Coverage',
    'Zero Deductible Option'
  ];

  const benefits = [
    'Protection against unexpected repair costs',
    'Roadside assistance wherever and whenever you need it',
    'Increased vehicle resale value',
    'Peace of mind while driving',
    'Customizable options to fit your needs'
  ];

  const europeanFeatures = [
    'Coverage for complex European vehicle electronics',
    'Protection for specialized components in BMW, Mercedes, Audi & Porsche',
    'Integration with our European vehicle service records',
    'Direct coordination with our European auto technicians'
  ];

  const faqs = [
    {
      question: 'When should I purchase a warranty plan?',
      answer: 'The best time to purchase a warranty is before your manufacturer\'s warranty expires or at the time of vehicle purchase. This ensures continuous coverage and often results in better rates.'
    },
    {
      question: 'Do your warranty plans cover European vehicle components?',
      answer: 'Yes! Our warranty plans are designed to work seamlessly with European vehicles. As Dallas\'s trusted European auto specialists, we ensure all warranty plans properly cover the sophisticated components found in BMW, Mercedes, Audi, and other European makes.'
    },
    {
      question: 'How does your warranty work with your European auto repair service?',
      answer: 'Our warranty plans are fully integrated with our European auto repair services. When your European vehicle needs service, simply bring it to our facility, and we\'ll handle both the repairs and warranty claims in one seamless process.'
    },
    {
      question: 'How do I file a claim if my vehicle breaks down?',
      answer: 'Simply bring your vehicle to ShedX or any ASE-certified repair facility, provide them with your warranty information, and we\'ll handle the rest. There\'s no need to pay upfront or file paperwork yourself.'
    },
    {
      question: 'Is my warranty transferable if I sell my vehicle?',
      answer: 'Yes! All our warranty plans are transferable to a new owner, which can significantly increase your vehicle\'s resale value and appeal to potential buyers.'
    }
  ];

  return (
    <>
      <Helmet>
        <title>Vehicle Warranty Plans | Extended Warranty Coverage | ShedX</title>
        <meta name="description" content="Protect your investment with comprehensive vehicle warranty plans from ShedX. Save up to $3,500 on repairs with coverage for European & domestic vehicles." />
        <meta name="keywords" content="vehicle warranty, extended warranty, car warranty plans, European vehicle warranty, auto warranty Dallas, ShedX warranty" />
        <link rel="canonical" href="https://shedx.com/warranty" />
        
        {/* Open Graph */}
        <meta property="og:title" content="Vehicle Warranty Plans - ShedX" />
        <meta property="og:description" content="Comprehensive vehicle warranty coverage. Save thousands on unexpected repairs. European vehicle specialists." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://shedx.com/warranty" />
        
        {/* Schema.org markup */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Product",
            "name": "Vehicle Warranty Plans",
            "description": "Comprehensive vehicle warranty coverage from ShedX",
            "provider": {
              "@type": "LocalBusiness",
              "name": "ShedX",
              "telephone": "+1-682-204-0561",
              "address": {
                "@type": "PostalAddress",
                "addressRegion": "TX",
                "addressCountry": "US"
              }
            }
          })}
        </script>
      </Helmet>

      {/* Hero Section */}
      <div className="relative bg-gradient-to-r from-primary-600 to-primary-800 py-20 overflow-hidden">
        <div className="absolute inset-0 bg-black opacity-20"></div>
        <div className="relative max-w-7xl mx-auto px-4 text-center text-white">
          <motion.div {...fadeInUp}>
            <h1 className="text-5xl md:text-6xl font-bold mb-4">Vehicle Warranty Plans</h1>
            <p className="text-xl md:text-2xl mb-8">Comprehensive protection for your investment from ShedX</p>
            <div className="inline-block bg-white text-primary-600 px-8 py-4 rounded-lg mb-8">
              <div className="text-sm">SAVE UP TO</div>
              <div className="text-4xl font-bold">$3,500</div>
              <div className="text-sm">ON REPAIRS</div>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="tel:+16822040561" className="bg-white text-primary-600 px-8 py-4 rounded-lg font-bold hover:bg-gray-100 transition-colors inline-flex items-center justify-center">
                <Phone className="mr-2" />
                Get Your Quote Today
              </a>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Main Content */}
      <div className="bg-gray-50 py-16">
        <div className="max-w-7xl mx-auto px-4">
          {/* Intro Section */}
          <motion.section {...fadeInUp} className="mb-16">
            <div className="bg-white rounded-xl p-8 shadow-lg">
              <h2 className="text-3xl font-bold mb-6 text-gray-900">Comprehensive Vehicle Warranty</h2>
              <p className="text-lg text-gray-700 mb-6">
                Don't let unexpected repairs derail your budget. Our comprehensive warranty plans keep you covered when you need it most.
              </p>
              
              <div className="bg-gradient-to-r from-primary-50 to-primary-100 rounded-lg p-6 mb-6">
                <h3 className="text-xl font-bold mb-4 text-gray-900">Get Your Car Warranty At ShedX</h3>
                <p className="text-gray-700 mb-4">
                  In conjunction with our European auto repair & Service Department, ShedX offers full warranty 
                  packages for your used cars. Don't take the chance of a costly repair. Let us do the service and let the warranty 
                  pick up the bill!
                </p>
              </div>
              
              <p className="text-gray-700 mb-4">
                At ShedX, we are a family-owned business dedicated to providing high-quality storage sheds 
                and outdoor buildings to customers across the United States. This commitment 
                extends to our comprehensive warranty coverage.
              </p>
              
              <p className="text-gray-700">
                We believe that trust is the foundation of every successful transaction. As a faith-based business, we operate 
                with the understanding that integrity matters in everything we do.
              </p>
            </div>
          </motion.section>

          {/* Why Choose Section */}
          <motion.section {...fadeInUp} className="mb-16">
            <h2 className="text-3xl font-bold mb-8 text-center text-gray-900">Why Choose Our Warranty Plans</h2>
            <p className="text-lg text-center text-gray-700 mb-12 max-w-3xl mx-auto">
              Protect your investment with comprehensive coverage designed to give you peace of mind.
            </p>
            
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-white p-6 rounded-lg shadow-lg text-center">
                <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Shield className="w-8 h-8 text-primary-600" />
                </div>
                <h3 className="text-xl font-bold mb-3 text-gray-900">Comprehensive Coverage</h3>
                <p className="text-gray-700">
                  From basic protection of critical components to bumper-to-bumper coverage, our plans are designed to fit your needs and budget.
                </p>
              </div>
              
              <div className="bg-white p-6 rounded-lg shadow-lg text-center">
                <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Car className="w-8 h-8 text-primary-600" />
                </div>
                <h3 className="text-xl font-bold mb-3 text-gray-900">Roadside Assistance</h3>
                <p className="text-gray-700">
                  Never worry about being stranded. Our roadside assistance helps wherever you are, whenever you need it.
                </p>
              </div>
              
              <div className="bg-white p-6 rounded-lg shadow-lg text-center">
                <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <DollarSign className="w-8 h-8 text-primary-600" />
                </div>
                <h3 className="text-xl font-bold mb-3 text-gray-900">Financial Protection</h3>
                <p className="text-gray-700">
                  Safeguard yourself against unexpected, costly repairs that can put a strain on your finances.
                </p>
              </div>
            </div>
          </motion.section>

          {/* Protection Plans */}
          <motion.section {...fadeInUp} className="mb-16">
            <h2 className="text-3xl font-bold mb-8 text-center text-gray-900">Vehicle Protection Plan Details</h2>
            <p className="text-lg text-center text-gray-700 mb-12 max-w-3xl mx-auto">
              Choose the coverage level that's right for you and your vehicle.
            </p>
            
            <div className="grid lg:grid-cols-3 gap-8">
              {/* Basic Plan */}
              <div className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                <div className="bg-gray-600 text-white p-6">
                  <h3 className="text-2xl font-bold mb-2">Basic Protection</h3>
                  <p className="text-gray-200">Coverage for the most expensive and critical vehicle components.</p>
                </div>
                <div className="p-6">
                  <ul className="space-y-3">
                    {basicFeatures.map((feature, index) => (
                      <li key={index} className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-0.5" />
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <a href="tel:+16822040561" className="mt-6 block text-center bg-gray-600 text-white px-6 py-3 rounded-lg hover:bg-gray-700 transition-colors">
                    Get Quote
                  </a>
                </div>
              </div>

              {/* Premium Plan */}
              <div className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow transform lg:scale-105">
                <div className="bg-primary-600 text-white p-6">
                  <div className="inline-block bg-yellow-400 text-primary-800 text-xs px-2 py-1 rounded mb-2">MOST POPULAR</div>
                  <h3 className="text-2xl font-bold mb-2">Premium Protection</h3>
                  <p className="text-primary-100">Expanded coverage for comprehensive vehicle protection.</p>
                </div>
                <div className="p-6">
                  <ul className="space-y-3">
                    {premiumFeatures.map((feature, index) => (
                      <li key={index} className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-0.5" />
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <a href="tel:+16822040561" className="mt-6 block text-center bg-primary-600 text-white px-6 py-3 rounded-lg hover:bg-primary-700 transition-colors">
                    Get Quote
                  </a>
                </div>
              </div>

              {/* Ultimate Plan */}
              <div className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                <div className="bg-red-600 text-white p-6">
                  <h3 className="text-2xl font-bold mb-2">Ultimate Protection</h3>
                  <p className="text-red-100">Our most comprehensive bumper-to-bumper coverage.</p>
                </div>
                <div className="p-6">
                  <ul className="space-y-3">
                    {ultimateFeatures.map((feature, index) => (
                      <li key={index} className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-0.5" />
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <a href="tel:+16822040561" className="mt-6 block text-center bg-red-600 text-white px-6 py-3 rounded-lg hover:bg-red-700 transition-colors">
                    Get Quote
                  </a>
                </div>
              </div>
            </div>
          </motion.section>

          {/* Used Vehicle Protection */}
          <motion.section {...fadeInUp} className="mb-16">
            <div className="bg-white rounded-xl p-8 shadow-lg">
              <h2 className="text-3xl font-bold mb-6 text-gray-900">Used Vehicle Protection Plans</h2>
              <p className="text-lg text-gray-700 mb-6">
                New vehicles may be covered by the manufacturer's warranty, but once that has run out, you are responsible for 
                any breakdowns that might occur. By adding a Used Vehicle Protection Plan to your vehicle, you will help offset 
                the cost of any repairs you may need.
              </p>
              
              <div className="bg-gray-50 rounded-lg p-6">
                <h3 className="text-xl font-bold mb-4 text-gray-900">Benefits of Our Warranty Plans:</h3>
                <ul className="space-y-3">
                  {benefits.map((benefit, index) => (
                    <li key={index} className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">{benefit}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </motion.section>

          {/* European Vehicle Coverage */}
          <motion.section {...fadeInUp} className="mb-16">
            <div className="bg-gradient-to-r from-blue-900 to-blue-800 rounded-xl p-8 text-white">
              <h2 className="text-3xl font-bold mb-6">European Vehicle Specialty Coverage</h2>
              <p className="text-xl mb-8 text-blue-100">
                Specialized warranty protection designed for luxury European vehicles serviced at our Dallas-area facility.
              </p>
              
              <div className="grid md:grid-cols-2 gap-6">
                {europeanFeatures.map((feature, index) => (
                  <div key={index} className="flex items-start">
                    <Shield className="w-6 h-6 text-yellow-400 mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-white">{feature}</span>
                  </div>
                ))}
              </div>
              
              <div className="mt-8 flex flex-col sm:flex-row gap-4">
                <a href="tel:+16822040561" className="bg-white text-blue-900 px-6 py-3 rounded-lg hover:bg-gray-100 transition-colors text-center font-bold">
                  Get European Vehicle Coverage
                </a>
                <Link to="/services/european-car-repair" className="bg-blue-700 text-white px-6 py-3 rounded-lg hover:bg-blue-600 transition-colors text-center font-bold">
                  View Our European Services
                </Link>
              </div>
            </div>
          </motion.section>

          {/* FAQ Section */}
          <motion.section {...fadeInUp} className="mb-16">
            <h2 className="text-3xl font-bold mb-8 text-center text-gray-900">Frequently Asked Questions</h2>
            <p className="text-lg text-center text-gray-700 mb-12 max-w-3xl mx-auto">
              Get answers to common questions about our warranty plans.
            </p>
            
            <div className="max-w-4xl mx-auto">
              {faqs.map((faq, index) => (
                <div key={index} className="bg-white rounded-lg shadow mb-4 overflow-hidden">
                  <div className="p-6">
                    <h3 className="text-xl font-bold mb-3 text-gray-900 flex items-start">
                      <AlertCircle className="w-6 h-6 text-primary-600 mr-3 mt-0.5 flex-shrink-0" />
                      {faq.question}
                    </h3>
                    <p className="text-gray-700 ml-9">{faq.answer}</p>
                  </div>
                </div>
              ))}
            </div>
          </motion.section>

          {/* CTA Section */}
          <motion.section {...fadeInUp}>
            <div className="bg-gradient-to-r from-primary-600 to-primary-700 rounded-xl p-12 text-white text-center">
              <h2 className="text-3xl font-bold mb-4">Protect Your Investment Today</h2>
              <p className="text-xl mb-8 max-w-3xl mx-auto">
                Don't wait until it's too late. A ShedX warranty can save you thousands in unexpected repair costs.
              </p>
              <p className="text-lg mb-8">
                Our warranty specialists will help you find the perfect plan for your vehicle and budget.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a 
                  href="tel:+16822040561" 
                  className="inline-flex items-center justify-center bg-white text-primary-600 px-8 py-4 rounded-lg hover:bg-gray-100 transition-colors text-xl font-bold"
                >
                  <Phone className="mr-3" />
                  Call (682) 204-0561
                </a>
                <Link 
                  to="/service" 
                  className="inline-flex items-center justify-center bg-primary-800 text-white px-8 py-4 rounded-lg hover:bg-primary-900 transition-colors text-xl font-bold"
                >
                  Schedule Consultation
                  <ArrowRight className="ml-3" />
                </Link>
              </div>
            </div>
          </motion.section>
        </div>
      </div>
    </>
  );
};

export default Warranty;
